import type { ApiAttachment, ApiAttachmentDownloadResponse } from '@Model';
import {
    EnumFilterExpressionOperation,
    isOk,
    type ListInfo,
    type ListRequest,
    type Result,
    useToasts,
    useSynqoHttp,
} from '@synqo/frontend-component-library';
import { useFileDownload } from '@Hooks/useFileDownload';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

/** List + download only. Unlike {@link useEntityAttachments}, never calls the upload-only /options endpoint. */
// @ai-generated 2026-07-13T00:00:00Z by philipp.kammerer@synqony.com using claude-opus-4-8
export function useReadOnlyEntityAttachments(baseUrl: string, entityPath: string, entityId: string | undefined) {
    const http = useSynqoHttp(`${baseUrl}/${entityPath}/${entityId ?? ''}/attachments`);
    const { triggerUrlDownload } = useFileDownload();
    const queryClient = useQueryClient();
    const { pushToast, pushAllToasts } = useToasts();

    const toastError = useCallback((message: string) => pushToast({ message, variant: 'error' }), [pushToast]);

    const listKey = useMemo(() => ['attachments', entityPath, entityId] as const, [entityPath, entityId]);

    const emptyListRequest = useMemo<ListRequest>(
        () => ({
            page_no: 0,
            page_size: 100,
            full_text_search: '',
            sort_columns: [],
            filter_columns: { operation: EnumFilterExpressionOperation.And, filters: [] },
        }),
        [],
    );

    const listQuery = useQuery({
        queryKey: listKey,
        queryFn: () => http.post<Result<ListInfo<ApiAttachment>>, ListRequest>(':query', emptyListRequest),
        enabled: !!entityId,
    });

    const invalidateList = useCallback(() => {
        void queryClient.invalidateQueries({ queryKey: listKey });
    }, [queryClient, listKey]);

    const downloadMutation = useMutation({
        mutationFn: async (attachmentId: string) => {
            const response = await http.get<Result<ApiAttachmentDownloadResponse>>(`/${attachmentId}:download`);
            if (!response || !isOk(response)) {
                if (response) {
                    pushAllToasts(response.messages);
                } else {
                    toastError('Download-Link konnte nicht geladen werden.');
                }
                throw new Error('Could not retrieve download URL');
            }
            triggerUrlDownload(response.data.download_url);
        },
    });

    const listData = listQuery.data;
    const attachments = listData && isOk(listData) ? listData.data.items : [];

    return {
        attachments,
        isLoading: listQuery.isLoading,
        download: downloadMutation.mutateAsync,
        invalidateList,
    };
}
